import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPrint, faSearch, faFileExcel, faSyncAlt, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import logoImage from '../assets/images/logo.jpg';
import QRCode from 'qrcode';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import Fuse from 'fuse.js';

const LocalCertificateList = () => {
    const navigate = useNavigate();
    const [gdsrEntries, setGdsrEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastRefreshTime, setLastRefreshTime] = useState(null);

    const newEntriesCount = useMemo(() => {
        return gdsrEntries.filter(entry => entry.isNew).length;
    }, [gdsrEntries]);

    const fuse = useMemo(() => new Fuse(gdsrEntries, {
        keys: ['token_id', 'local_no', 'name', 'father_name', 'cnic', 'phone_no', 'status'],
        threshold: 0.3,
        includeScore: true
    }), [gdsrEntries]);

    const fetchEntries = useCallback(async () => {
        try {
            console.log("Fetching local certificate entries...");
            const response = await axios.post('https://chaman.gob.pk/eac_backend/localcertificate.php', {
                action: 'get_records',
            });

            console.log("Response received:", response.data);

            // Check if response has data property (new structure)
            if (response.data && response.data.status === "success" && Array.isArray(response.data.data)) {
                const now = new Date();
                setLastRefreshTime(now);

                const sortedEntries = response.data.data
                    .map(entry => {
                        const timestamp = new Date(entry.date);
                        return {
                            ...entry,
                            timestamp: isNaN(timestamp.getTime()) ? new Date(0) : timestamp
                        };
                    })
                    .sort((a, b) => b.timestamp - a.timestamp);

                setGdsrEntries(prevEntries => {
                    const existingIds = new Set(prevEntries.map(item => item.token_id));

                    const newEntries = sortedEntries
                        .filter(entry => !existingIds.has(entry.token_id))
                        .map(entry => ({ ...entry, isNew: true }));

                    const existingEntries = prevEntries
                        .map(entry => ({ ...entry, isNew: false }));

                    return [...newEntries, ...existingEntries];
                });
            } else if (Array.isArray(response.data)) {
                // Handle old structure (direct array)
                console.log("Using old response structure (direct array)");
                const now = new Date();
                setLastRefreshTime(now);

                const sortedEntries = response.data
                    .map(entry => {
                        const timestamp = new Date(entry.date);
                        return {
                            ...entry,
                            timestamp: isNaN(timestamp.getTime()) ? new Date(0) : timestamp
                        };
                    })
                    .sort((a, b) => b.timestamp - a.timestamp);

                setGdsrEntries(prevEntries => {
                    const existingIds = new Set(prevEntries.map(item => item.token_id));

                    const newEntries = sortedEntries
                        .filter(entry => !existingIds.has(entry.token_id))
                        .map(entry => ({ ...entry, isNew: true }));

                    const existingEntries = prevEntries
                        .map(entry => ({ ...entry, isNew: false }));

                    return [...newEntries, ...existingEntries];
                });
            } else {
                setGdsrEntries([]);
                console.error('Unexpected response format:', response.data);
                setError('Unexpected data format received from server');
            }
        } catch (err) {
            console.error('Error fetching entries:', err);
            setError('Failed to fetch Local Certificate entries: ' + (err.message || 'Network error'));
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchEntries();
        }, 5);

        return () => clearTimeout(timer);
    }, [fetchEntries]);

    const refreshData = () => {
        setIsRefreshing(true);
        fetchEntries();
    };

    const filteredEntries = useMemo(() => {
        if (!searchTerm) return gdsrEntries;
        return fuse.search(searchTerm).map(result => result.item);
    }, [gdsrEntries, searchTerm, fuse]);

    const handleDelete = async (token_id) => {
        if (window.confirm(`Are you sure you want to delete the entry with Token ID: ${token_id}?`)) {
            try {
                const response = await axios.post('https://chaman.gob.pk/eac_backend/localcertificate.php', {
                    action: 'delete',
                    token_id,
                });
                if (response.data.status === 'success') {
                    setGdsrEntries(prev => prev.filter(entry => entry.token_id !== token_id));
                } else {
                    throw new Error(response.data.message || 'Failed to delete');
                }
            } catch (error) {
                alert(`Error: ${error.message}`);
            }
        }
    };

    const handleEdit = (entry) => {
        navigate('/dashboard/edit-local-certificate', { state: { entry } });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const handlePrint = async (entry) => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        // Draw decorative border
        doc.setLineWidth(1.0);
        doc.setDrawColor(0, 0, 0);
        doc.rect(10, 10, pageWidth - 20, pageHeight - 20, 'S');

        // Add logo image and text
        doc.setFontSize(10);
        const imageWidth = 0.8 * 25.4;
        const imageHeight = 0.6 * 25.4;
        const xCenter = (pageWidth - imageWidth) / 2;
        doc.addImage(logoImage, 'PNG', xCenter, 20, imageWidth, imageHeight);

        doc.setFontSize(21.5);
        doc.setFont("normal");
        doc.text('LOCAL CERTIFICATE', 65, 47);

        doc.setFontSize(15);
        doc.setFont("normal");
        doc.text('OFFICE OF THE DEPUTY COMMISSIONER CHAMAN', 40, 55);

        doc.setLineWidth(0.5);
        doc.setDrawColor(0, 0, 0);
        doc.line(14, 58, 195, 58);

        doc.setFontSize(11);
        doc.text(`Local No.: ${entry.local_no || ''}`, 14, 63);
        doc.text(`Date: ${formatDate(entry.date)}`, 168, 63);

        // Table data
        const tableData = [
            ["Name", entry.name || ''],
            ["Father Name", entry.father_name || ''],
            ["CNIC", entry.cnic || ''],
            ["Phone No.", entry.phone_no || ''],
        ];

        doc.autoTable({
            body: tableData,
            startY: 75,
            theme: 'grid',
            headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255] },
            styles: {
                fontSize: 12,
                textColor: [0, 0, 0],
                lineColor: [0, 0, 0],
            },
            columnStyles: {
                0: { cellWidth: 30, halign: 'left' },
                1: { fontStyle: 'bold' },
            },
        });

        doc.setFontSize(11);
        doc.text("DEPUTY COMMISSIONER", 148, pageHeight - 175);
        doc.text("CHAMAN", 163, pageHeight - 170);

        // QR Code Generation
        const qrData = `Local certificate of ${entry.name || ''} has been verified and issued by the Office of the Deputy Commissioner, Chaman.`;
        const qrCanvas = document.createElement('canvas');

        await QRCode.toCanvas(qrCanvas, qrData, { width: 100 });
        const qrDataURL = qrCanvas.toDataURL('image/png');

        const qrX = 100;
        const qrY = pageHeight - 80;

        doc.addImage(qrDataURL, 'PNG', qrX, qrY, 20, 20);

        window.open(doc.output('bloburl'));
    };

    const Row = ({ index, style }) => {
        const entry = filteredEntries[index];
        if (!entry) return null;

        return (
            <div style={style} className="table-row">
                <div className="table-cell">{entry.token_id || 'N/A'}</div>
                <div className="table-cell">{formatDate(entry.date)}</div>
                <div className="table-cell">{entry.name || 'N/A'}</div>
                <div className="table-cell">{entry.father_name || 'N/A'}</div>
                <div className="table-cell">{entry.cnic || 'N/A'}</div>
                <div className="table-cell actions btn-group">
                    <button onClick={() => handlePrint(entry)} title="Print" className="print-btn">
                        <FontAwesomeIcon icon={faPrint} />
                    </button>
                    <button onClick={() => handleEdit(entry)} title="Edit" className="edit-btn">
                        <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button onClick={() => handleDelete(entry.token_id)} title="Delete" className="delete-btn">
                        <FontAwesomeIcon icon={faTrash} />
                    </button>
                </div>
            </div>
        );
    };

    if (loading) return (
        <div className="loading-container">
            <div className="modern-loading">
                <div className="loading-wave">
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                </div>
                <p className="loading-text">Loading Local Certificate Records...</p>
            </div>
        </div>
    );

    if (error) return (
        <div className="error-container">
            <div className="error-card">
                <h3>Error Loading Data</h3>
                <p>{error}</p>
                <div className="error-actions">
                    <button onClick={refreshData} className="retry-btn">
                        <FontAwesomeIcon icon={faSyncAlt} /> Retry
                    </button>
                    <button onClick={() => window.location.reload()} className="reload-btn">
                        Reload Page
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="certificate-dashboard">
            <div className="dashboard-header">
                <div className="header-left">
                    <h2>Local Certificate Records</h2>
                    <div className="stats-container">
                        <span className="entries-count">Total: {gdsrEntries.length}</span>
                        {newEntriesCount > 0 && (
                            <span className="new-entries-count">New: {newEntriesCount}</span>
                        )}
                        {lastRefreshTime && (
                            <span className="refresh-time">
                                Last updated: {lastRefreshTime.toLocaleTimeString()}
                            </span>
                        )}
                    </div>
                </div>

                <div className="header-right">
                    <div className="search-refresh-wrapper">
                        <button
                            onClick={refreshData}
                            className="refresh-button"
                            disabled={isRefreshing}
                            title="Refresh data"
                        >
                            <FontAwesomeIcon
                                icon={faSyncAlt}
                                spin={isRefreshing}
                                className={`refresh-icon ${isRefreshing ? 'spinning' : ''}`}
                            />
                            {isRefreshing ? 'Refreshing...' : 'Refresh'}
                        </button>

                        <div className="search-bar">
                            <FontAwesomeIcon icon={faSearch} className="search-icon" />
                            <input
                                type="text"
                                placeholder="Search by Token ID, Name, CNIC..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                            {searchTerm && (
                                <button className="clear-search" onClick={() => setSearchTerm('')}>
                                    ×
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="table-container">
                <div className="table-header">
                    <div className="header-cell">Token ID</div>
                    <div className="header-cell">Date</div>
                    <div className="header-cell">Name</div>
                    <div className="header-cell">F/H Name</div>
                    <div className="header-cell">CNIC</div>
                    <div className="header-cell">Actions</div>
                </div>

                <div className="table-body">
                    {filteredEntries.length > 0 ? (
                        <AutoSizer>
                            {({ height, width }) => (
                                <List
                                    height={height}
                                    itemCount={filteredEntries.length}
                                    itemSize={45}
                                    width={width}
                                >
                                    {Row}
                                </List>
                            )}
                        </AutoSizer>
                    ) : (
                        <div className="empty-state">
                            {searchTerm ? (
                                <>
                                    <FontAwesomeIcon icon={faSearch} size="3x" />
                                    <p>No records found matching "{searchTerm}"</p>
                                    <button onClick={() => setSearchTerm('')} className="clear-search-btn">
                                        Clear Search
                                    </button>
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faInfoCircle} size="3x" />
                                    <p>No local certificate records available</p>
                                    <p className="empty-subtext">Add new records using the Local Certificate form</p>
                                </>
                            )}
                        </div>
                    )}
                </div>

                <div className="table-footer">
                    <div className="footer-info">
                        Showing {filteredEntries.length} of {gdsrEntries.length} entries
                        {searchTerm && ` (filtered)`}
                    </div>
                    <div className="footer-actions">
                        <button 
                            onClick={() => {
                                const ws = XLSX.utils.json_to_sheet(filteredEntries);
                                const wb = XLSX.utils.book_new();
                                XLSX.utils.book_append_sheet(wb, ws, "Local Certificate Records");
                                XLSX.writeFile(wb, `local_certificate_records_${new Date().toISOString().split('T')[0]}.xlsx`);
                            }}
                            className="export-btn"
                        >
                            <FontAwesomeIcon icon={faFileExcel} /> Export to Excel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LocalCertificateList;